import { model as i18n } from '!/i18n'
import { model as cartModel } from '$/cart'
import { model as paymentInfoModel } from '$/payment-info'
import { model as priceSettingsModel } from '$/price-settings'
import { model as redirectUrlModel } from '$/redirect-url'
import { api } from '@setplex/tria-api'
import { createEvent, sample } from 'effector'
import type { TvShowBundle, TvShowPayment } from '../../interfaces'
import {
  getPerItemPriceSettingsOnly,
  getPerSetPriceSettingsOnly,
  isPayForBundle,
  isPayForItem,
} from '../../lib'
import { anyPaymentInitiated } from './model'

export const selectTvShowPaymentButton = createEvent<TvShowPayment>()

export const payForTvShow = createEvent<TvShowPayment>()

export const payForTvShowBundle = createEvent<TvShowBundle>()

// TODO: seasonId and episodeId not actual for current time, when there is no redirect
sample({
  clock: selectTvShowPaymentButton,
  fn: ({ id, seasonId, episodeId }) => ({ id, seasonId, episodeId }),
  target: redirectUrlModel.saveTvShowRedirectUrl,
})

sample({
  clock: selectTvShowPaymentButton,
  filter: isPayForItem,
  fn: ({ priceSettings, ...rest }) => ({
    priceSettings: getPerItemPriceSettingsOnly(priceSettings),
    ...rest,
  }),
  target: payForTvShow,
})

sample({
  clock: selectTvShowPaymentButton,
  filter: isPayForBundle,
  fn: ({ priceSettings }) => ({
    priceSettings: getPerSetPriceSettingsOnly(priceSettings),
  }),
  target: payForTvShowBundle,
})

sample({
  clock: [payForTvShow, payForTvShowBundle],
  fn: ({ priceSettings }) => priceSettings,
  target: priceSettingsModel.$priceSettings,
})

sample({
  clock: [payForTvShow, payForTvShowBundle],
  target: anyPaymentInitiated,
})

sample({
  clock: payForTvShow,
  source: {
    i18n: i18n.$t,
  },
  fn: ({ i18n: { t } }, clockData) => {
    const labels = [clockData.ageRatings, String(clockData.year)].filter(
      Boolean
    )

    return {
      name: clockData.name,
      title: t('series'),
      imageUrl: clockData.imageUrl,
      labels: labels?.length > 0 ? labels : null,
      priceSettings: clockData.priceSettings,
    }
  },
  target: paymentInfoModel.$uiPaymentInfo,
})

sample({
  clock: payForTvShow,
  fn: (tvShow) => ({
    tvShowId: tvShow.id,
  }),
  target: cartModel.$tvShowInfoForCart,
})

sample({
  clock: payForTvShowBundle,
  fn: ({ priceSettings }) => ({
    bundleId: priceSettings?.[0].bundle?.id ?? 0,
  }),
  target: api.showBundles.loadShowBundleInfoForContent,
})

sample({
  clock: api.showBundles.loadShowBundleInfoForContent.doneData,
  source: {
    priceSettings: priceSettingsModel.$priceSettings,
    i18n: i18n.$t,
  },
  filter: ({ priceSettings }) => Boolean(priceSettings),
  fn: ({ i18n: { t } }, bundleInfo) => {
    const itemCountLabel = t('series_amount', {
      amount: bundleInfo.contentQuantity,
    })
    return {
      name: bundleInfo.name,
      title: t('extras'),
      imageUrl: bundleInfo.landscapeImageUrl || '',
      labels: [itemCountLabel],
      priceSettings: bundleInfo?.priceSettings ?? null,
      bundlePoster: true,
    }
  },
  target: paymentInfoModel.$uiPaymentInfo,
})

sample({
  clock: api.showBundles.loadShowBundleInfoForContent.doneData,
  source: priceSettingsModel.$priceSettings,
  filter: (priceSettings) => Boolean(priceSettings),
  fn: (_, bundleInfo) => ({
    tvShowSetId: bundleInfo.id,
    tvShowIds: bundleInfo.tvShowIds,
    seasonIds: bundleInfo.seasonIds,
    episodeIds: bundleInfo.episodeIds,
  }),
  target: cartModel.$tvShowBundleInfoForCart,
})
